import { useBankAccountsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/useBankAccountsController.ts';
import { getMipInvestorBankAccounts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccounts.api.ts';
import { BankAccountExportSelection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/bankAccountsExport.types.ts';
import {
	buildBankAccountsWorkbookBlob,
	toExportRows,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/bankAccountsExport.utils.ts';
import { BulkOperationType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils';
import {
	getValidationOutcome,
	VALIDATION_OUTCOME_LABEL_KEYS,
	VERIFICATION_BUCKET_LABEL_KEYS,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/BankAccounts.utils.ts';
import { Table as BankAccountsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/Table.tsx';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getBankAccountVerificationBucket } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { Flex } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

export const BankAccounts = () => {
	const tMipManage = useTranslationMipManage();
	const { deal, mipData } = useMIPManageContext();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const navigate = useNavigate();
	const { search } = useLocation();

	const { dataOperations, request, bankAccounts, stats, isLoading, isFetching } =
		useBankAccountsController();

	// The table only holds the page in front of the user, so the selection travels as an
	// intent and the server resolves it against the same filters, search and sort the table
	// is showing — a "select all" then covers rows this browser never loaded.
	const resolveSelectedBankAccounts = async (selection: BankAccountExportSelection) => {
		const response = await getMipInvestorBankAccounts({
			...request,
			offset: 0,
			limit: stats?.total || request.limit,
			...(selection.operationType === BulkOperationType.selected
				? { selectedIds: selection.selectedIds }
				: { excludedIds: selection.excludedIds }),
		});

		return response.payload?.bankAccounts ?? [];
	};

	const { mutateAsync: downloadBankAccounts, isPending: isDownloading } = useMutation({
		mutationFn: async (selection: BankAccountExportSelection) => {
			const selectedBankAccounts = await resolveSelectedBankAccounts(selection);
			const rows = toExportRows(selectedBankAccounts, {
				validation: (account) =>
					tMipManage(VALIDATION_OUTCOME_LABEL_KEYS[getValidationOutcome(account)]),
				status: (account) => {
					const bucket = getBankAccountVerificationBucket(account.status);

					return bucket ? tMipManage(VERIFICATION_BUCKET_LABEL_KEYS[bucket]) : '';
				},
			});

			return await buildBankAccountsWorkbookBlob(rows, {
				accountHolder: tMipManage('bank_accounts.table.account_holder'),
				bankName: tMipManage('bank_accounts.export.bank_name'),
				country: tMipManage('bank_accounts.export.country'),
				currency: tMipManage('bank_accounts.export.currency'),
				accountDetails: tMipManage('bank_accounts.table.account_details'),
				validation: tMipManage('bank_accounts.table.validation'),
				verificationStatus: tMipManage('bank_accounts.table.verification_status'),
				owner: tMipManage('bank_accounts.table.owner'),
				ownerEmail: tMipManage('bank_accounts.export.owner_email'),
				lastUpdated: tMipManage('bank_accounts.table.last_updated'),
			});
		},
		onSuccess: (blob) => {
			const fileName = `${deal?.name ?? 'unknown'}-${
				mipData?.entityName ?? ''
			}-bank-accounts.xlsx`;
			downloadFileFromBlob(blob, fileName);
		},
	});

	// `search` carries the filters over, so the details page pages the same slice.
	const openBankAccount = (bankAccountId: string) =>
		navigate({
			pathname: mipManagePath(MIP_MANAGE_ROUTES.BANK_ACCOUNT_DETAILS, {
				dealId,
				mipId,
				bankAccountId,
			}),
			search,
		});

	return (
		<Flex pos="relative" direction="column" w="100%" h="100%">
			{(isLoading || isFetching) && <InlineLoader />}

			<BankAccountsTable
				bankAccounts={bankAccounts}
				stats={stats}
				dataOperations={dataOperations}
				isLoading={isLoading}
				onDownload={(selection) => void downloadBankAccounts(selection)}
				onRowClick={openBankAccount}
				isDownloading={isDownloading}
			/>
		</Flex>
	);
};
